import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import swal from 'sweetalert';

import { useTheme } from "../includes/theme-provider.js";
import { getCookie } from '../common_component/cookie.js';
import { useModal } from '../providers/modal-provider.js';
import { useAuthData } from '../includes/auth-provider.js';

export default function RevertModal() {
	const { theme } = useTheme();
	const { displayModal, modalData, closeModal, setModalView, setModalData } = useModal();

	const [loading, setLoading] = useState(false);
	const [revertData, setRevertData] = useState(null);
	const [errorMsg, setErrorMsg] = useState(null);
	const [confirmLoading, setConfirmLoading] = useState(false);

	const pageName = modalData?.page_name || "";
	const { write, organization_id } = useAuthData(pageName);

	useEffect(() => {
		if (displayModal && modalData && modalData.customer_invoice_id) {
			fetchRevertDetails();
		}
	}, [displayModal, modalData]);

	const fetchRevertDetails = async () => {
		setLoading(true);
		setErrorMsg(null);
		setRevertData(null);
		try {
			const headers = {
				headers: {
					"Content-Type": "application/json",
					"Accept": "*/*",
					"token": getCookie("access_token")
				}
			};
			const payload = {
				page_name: pageName,
				customer_invoice_id: modalData.customer_invoice_id,
				organization_id: modalData.organization_id || organization_id
			};

			const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/get-customer-invoice-details-by-status`, payload, headers);
			if (res.data.operation === 'success') {
				setRevertData(res.data.data);
			} else {
				setErrorMsg(res.data.message || 'Failed to fetch revert status details.');
			}
		} catch (err) {
			console.error(err);
			setErrorMsg('An error occurred while checking revert dependencies.');
		} finally {
			setLoading(false);
		}
	};

	const getRevertEndpoint = () => {
		if (pageName.includes('invoice')) {
			return 'revert-customer-invoice';
		} else if (pageName.includes('warehouse')) {
			return 'revert-customer-warehouse-processing';
		} else {
			return 'revert-customer-delivery-challan';
		}
	};

	const handleExecuteRevert = async () => {
		setConfirmLoading(true);
		try {
			const endpoint = getRevertEndpoint();
			const headers = {
				headers: {
					"Content-Type": "application/json",
					"Accept": "*/*",
					"token": getCookie("access_token")
				}
			};

			let payload = {
				organization_id: modalData.organization_id || organization_id,
				page_name: pageName
			};

			if (endpoint === 'revert-customer-delivery-challan') {
				const activeChallans = revertData.customer_delivery_challan_data?.filter(
					(dc) => dc.add_status === 'CUSTOMER_DELIVERY_CHALLAN_DISPATCHED'
				) || [];
				payload.delivery_challan_id = modalData.delivery_challan_id || activeChallans[0]?.delivery_challan_id;
			} else if (endpoint === 'revert-customer-warehouse-processing') {
				payload.warehouse_processing_id = revertData.customer_warehouse_processing_data?.warehouse_processing_id;
			} else {
				payload.customer_invoice_id = modalData.customer_invoice_id;
			}

			const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/${endpoint}`, payload, headers);
			setConfirmLoading(false);

			if (res.data.operation === "success") {
				swal("Great!", "Reverted successfully!", "success");
				handleRevertModalClose();
				modalData.apiCallback?.(res.data.info);
			} else {
				swal("Oops!", res.data.message || "Failed to revert.", "error");
			}
		} catch (error) {
			console.log(error);
			setConfirmLoading(false);
			swal("Oops!", "An error occurred during the revert action.", "error");
		}
	};

	const handleRevertModalClose = () => {
		modalData?.modalCloseCallback?.();
		setLoading(false);
		setRevertData(null);
		setErrorMsg(null);
		setConfirmLoading(false);
		closeModal();
		setModalView(null);
		setModalData(null);
	};

	// Helper to check if all items from invoice are processed/challaned
	const areAllItemsProcessed = () => {
		if (!revertData) return false;
		const invoiceItems = revertData.customer_invoice_data?.item_array || [];
		const warehouseItems = revertData.customer_warehouse_processing_data?.item_array || [];

		if (invoiceItems.length === 0) return true;
		if (warehouseItems.length === 0) return false;

		// Basic verification matching item IDs and quantities
		return invoiceItems.every(invItem => {
			const match = warehouseItems.find(wItem => wItem.item_id === invItem.item_id);
			return match && match.quantity >= invItem.quantity;
		});
	};

	const areAllItemsChallaned = () => {
		if (!revertData) return false;
		const invoiceItems = revertData.customer_invoice_data?.item_array || [];
		const challans = revertData.customer_delivery_challan_data?.filter(
			dc => dc.add_status === 'CUSTOMER_DELIVERY_CHALLAN_DISPATCHED'
		) || [];

		if (invoiceItems.length === 0) return true;
		if (challans.length === 0) return false;

		// 1. Flatten all items from active challans
		const challanItems = challans.flatMap(dc => dc.item_array || []);

		// 2. Aggregate quantities by item_id across all challans
		const dispatchedQtyMap = {};
		challanItems.forEach(cItem => {
			const id = cItem.item_id;
			const qty = Number(cItem.quantity) || 0;
			dispatchedQtyMap[id] = (dispatchedQtyMap[id] || 0) + qty;
		});

		//console.log("Dispatched Totals Map:", dispatchedQtyMap);

		// 3. Verify if every invoice item's required quantity is fully met
		return invoiceItems.every(invItem => {
			const totalDispatched = dispatchedQtyMap[invItem.item_id] || 0;
			return totalDispatched >= invItem.quantity;
		});
	};

	const renderDependencyCheck = () => {
		if (!revertData) return null;
		const { customer_warehouse_processing_data, customer_delivery_challan_data, customer_invoice_data } = revertData;

		// SCENARIO 3: Invoice List Page Check
		if (pageName.includes('invoice')) {
			if (customer_warehouse_processing_data && customer_invoice_data.add_status === 'CUSTOMER_INVOICE_WAREHOUSE') {
				return (
					<div className="alert alert-warning">
						<h5 className="text-danger fw-bold">Action Required</h5>
						<p>You need to process the warehouse items first before reverting this invoice.</p>
					</div>
				);
			}
			// if (!areAllItemsProcessed()) {
			// 	return (
			// 		<div className="alert alert-warning">
			// 			<h5 className="text-danger fw-bold">Action Required</h5>
			// 			<p>Not all items of the invoice are warehouse processed yet.</p>
			// 		</div>
			// 	);
			// }
		}

		// SCENARIO 2: Warehouse Process Page Check
		if (pageName.includes('warehouse')) {
			if (!areAllItemsProcessed()) {
				return (
					<div className="alert alert-warning">
						<h5 className="text-danger fw-bold">Action Required</h5>
						<p>All items of the invoice must be warehouse processed first.</p>
					</div>
				);
			}

			if (customer_delivery_challan_data && customer_delivery_challan_data.length > 0) {
				const activeChallans = customer_delivery_challan_data.filter(
					(dc) => dc.add_status === 'CUSTOMER_DELIVERY_CHALLAN_DISPATCHED'
				);
				if (activeChallans.length > 0) {
					return (
						<div className="alert alert-warning">
							<h5 className="text-danger fw-bold">Action Required</h5>
							<p>You need to revert the following Delivery Challan(s) first:</p>
							<ul className="mb-0">
								{activeChallans.map((dc) => (
									<li key={dc.delivery_challan_id}>
										Challan #: <strong>{dc.delivery_challan_number || dc.delivery_challan_id}</strong>
									</li>
								))}
							</ul>
						</div>
					);
				}
			}
		}

		// SCENARIO 1: Delivery Challan List Page Check (Sales return, credit note, & all items challaned)
		if (pageName.includes('challan') || pageName.includes('delivery')) {
			if (customer_invoice_data?.has_sales_return || customer_invoice_data?.has_credit_note) {
				return (
					<div className="alert alert-warning">
						<h5 className="text-danger fw-bold">Action Required</h5>
						<p>Cannot revert delivery challan because a Sales Return or Credit Note is linked to this transaction.</p>
					</div>
				);
			}
			// if (!areAllItemsChallaned()) {
			// 	return (
			// 		<div className="alert alert-warning">
			// 			<h5 className="text-danger fw-bold">Action Required</h5>
			// 			<p>Not all items of the invoice have been delivery challaned yet.</p>
			// 		</div>
			// 	);
			// }
		}

		return (
			<div className="alert alert-success">
				<h5 className="text-success fw-bold">Ready to Revert</h5>
				<p>All item quantities, dependencies, and validation flags are cleared. You can safely proceed.</p>
			</div>
		);
	};

	const hasBlockingDependency = () => {
		if (!revertData) return true;
		const { customer_warehouse_processing_data, customer_delivery_challan_data, customer_invoice_data } = revertData;

		if (pageName.includes('invoice')) {
			return customer_warehouse_processing_data && customer_invoice_data.add_status === 'CUSTOMER_INVOICE_WAREHOUSE'
			//|| customer_warehouse_processing_data.add_status === 'CUSTOMER_WAREHOUSE_PROCESSED' || !areAllItemsProcessed();
		}

		if (pageName.includes('warehouse')) {
			if (!areAllItemsProcessed()) return true;
			const activeChallans = customer_delivery_challan_data?.filter(
				(dc) => dc.add_status === 'CUSTOMER_DELIVERY_CHALLAN_DISPATCHED'
			) || [];
			return activeChallans.length > 0;
		}

		if (pageName.includes('challan') || pageName.includes('delivery')) {
			return !!(customer_invoice_data?.has_sales_return || customer_invoice_data?.has_credit_note)
			//|| !areAllItemsChallaned();
		}

		return false;
	};

	return (
		<Modal show={displayModal} onHide={handleRevertModalClose} size="lg" centered>
			<Modal.Header>
				<Modal.Title>Revert Verification & Confirmation</Modal.Title>
				<div className="btn btn-sm btn-icon btn-active-color-primary" onClick={handleRevertModalClose}>
					<span className="svg-icon svg-icon-1">
						<svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
							<g transform="translate(12.000000, 12.000000) rotate(-45.000000) translate(-12.000000, -12.000000) translate(4.000000, 4.000000)" fill="#000000">
								<rect fill="#000000" x="0" y="7" width="16" height="2" rx="1" />
								<rect fill="#000000" opacity="0.5" transform="translate(8.000000, 8.000000) rotate(-270.000000) translate(-8.000000, -8.000000)" x="0" y="7" width="16" height="2" rx="1" />
							</g>
						</svg>
					</span>
				</div>
			</Modal.Header>
			<Modal.Body>
				<div className='container'>
					{loading ? (
						<div className="text-center py-5">
							<div className="spinner-border text-primary" role="status"></div>
							<p className="text-muted mt-2">Checking dependency status...</p>
						</div>
					) : errorMsg ? (
						<div className="alert alert-danger">{errorMsg}</div>
					) : (
						<div>{renderDependencyCheck()}</div>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-secondary rounded" onClick={handleRevertModalClose}>Cancel</button>
				{
					write && revertData && !loading && !hasBlockingDependency() &&
					<button
						className="btn btn-danger rounded"
						data-kt-indicator={confirmLoading ? "on" : "off"}
						disabled={confirmLoading}
						onClick={handleExecuteRevert}
					>
						<span className="indicator-label">Confirm Revert</span>
						<span className="indicator-progress">Please wait...
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					</button>
				}

			</Modal.Footer>
		</Modal>
	);
};